import React, {
	useState,
	useMemo,
	useEffect,
} from 'react';
import {
	Space,
	Typography,
} from 'antd';
import {
	DeleteOutlined,
	EditOutlined,
	SyncOutlined,
} from '@ant-design/icons';
import { SortOrder } from 'antd/es/table/interface';
import { Moment } from 'moment';
import {
	AccountingSystemProviders,
	CargoContractSides,
	Currencies,
	DATE,
	FeatureFlags,
	FixtureTypes,
	VoyagePaymentTypes,
} from '@shared/utils/constants';
import { calculateTotal } from '@shared/utils/math';
import { formatCurrency } from '@shared/utils/currency';
import { standardSort } from '@shared/utils/standardSort';
import { Values } from '@shared/utils/objectEnums';
import type { UpdateVoyagePaymentRequest } from '@api/features/voyages/updateVoyagePayment';
import type Cargo from '@api/models/cargo';
import { VoyagePaymentPartReturnType, VoyagePaymentReturnType } from '@api/utils/sequelize/getVoyagePayments';
import { formatDate } from '@client/utils/formatDate';
import AddButton from '@client/components/AddButton';
import Table from '@client/components/Table/Table';
import showErrorNotification from '@client/utils/showErrorNotification';
import FormPopover from '@client/components/FormPopover';
import showSuccessNotification from '@client/utils/showSuccessNotification';
import {
	deleteVoyagePayment,
	createVoyagePayment,
	updateVoyagePayment,
	syncPayments,
} from '@client/lib/api';
import Button from '@client/components/Button';
import { useAuth } from '@client/lib/auth';
import TooltipIcon from '@client/components/TooltipIcon';
import Card from '@client/components/Card/Card';
import Select from '@client/components/Select';
import useFeatureFlag from '@client/utils/hooks/useFeatureFlag';
import validatePaymentInvoiceInput from '../helpers/validatePaymentInvoiceInput';
import styles from './styles/PaymentsTable.module.css';
import PaymentExchangeRateInput from './PaymentExchangeRateInput';
import PaymentInvoiceInput from './PaymentInvoiceInput';
import PaymentPartRow from './PaymentPartRow';
import { useVoyage } from './VoyageProvider/VoyageProvider';

type PaymentsAndParts = VoyagePaymentReturnType;

type Props = {
	isEua?: boolean;
	refresh?: () => void;
	cargoContractSide?: CargoContractSides;
};

const PaymentsTable = ({
	isEua,
	refresh,
	cargoContractSide,
}: Props) => {
	const usesPaymentsSync = useFeatureFlag(FeatureFlags.PAYMENTS_SYNC);
	const {
		voyageDetails,
		voyageId,
		refreshDetails,
		fixtureType,
	} = useVoyage();

	const [formAmount, setFormAmount] = useState<number | null>(null);
	const { userInfo } = useAuth();

	const canSyncTcInPayments = userInfo.accountingSystemProvider === AccountingSystemProviders['365fo'];

	const isCargoReletInvoice = useMemo(() => {
		if (cargoContractSide === CargoContractSides.VC_IN) {
			return true;
		}

		if (cargoContractSide === CargoContractSides.VC_OUT) {
			return false;
		}

		return null;
	}, [cargoContractSide]);

	const hireInvoices = useMemo(() => {
		if (isEua) {
			return voyageDetails.euaInvoices ?? [];
		}

		const activeInvoices = (voyageDetails.hireInvoices ?? []).filter(
			(invoice) => invoice.deletedAt == null,
		);

		if (isCargoReletInvoice == null) {
			return activeInvoices;
		}

		return activeInvoices.filter(invoice => (
			invoice.isCargoRelet === isCargoReletInvoice
		));
	}, [
		isEua,
		isCargoReletInvoice,
		voyageDetails.hireInvoices,
		voyageDetails.euaInvoices,
	]);

	const payments = useMemo(() => {
		const allPayments = isEua ?
			voyageDetails.euaInvoicePayments ?? [] :
			voyageDetails.hireInvoicePayments ?? [];

		if (isEua || isCargoReletInvoice == null) {
			return allPayments;
		}

		const invoiceIds = new Set(hireInvoices.map(invoice => invoice.id));

		return allPayments.filter(payment => (
			payment.parts.some(part => invoiceIds.has(part.hireInvoiceId))
		));

	}, [
		isEua,
		isCargoReletInvoice,
		hireInvoices,
		voyageDetails.euaInvoicePayments,
		voyageDetails.hireInvoicePayments,
	]);

	const currencySource = useMemo(() => (voyageDetails as any)?.estimate ?? voyageDetails?.fixture,
		[voyageDetails]);

	const uniqueCurrencies = useMemo(() => {
		if (isEua) {
			return [Currencies.EUA];
		}

		if (!currencySource) {
			return [];
		}

		const currency = currencySource.currency ?? voyageDetails?.bankAccount?.currency;

		return Array.from(
			new Set([
				currency,
				...(currencySource.cargos?.map((cargo: Cargo) => cargo.currency) ?? []),
			]),
		).filter(Boolean);
	}, [isEua, currencySource, voyageDetails?.bankAccount?.currency]);

	const deletePayment = async (id: number) => {
		try {
			await deleteVoyagePayment(voyageId, id);
			refreshDetails();
			refresh?.();

			showSuccessNotification(
				'Payment deleted',
			);
		} catch (e) {
			showErrorNotification('Could not delete payment', e as Error);
		}
	};

	const editPayment = async (id: number, attributes: UpdateVoyagePaymentRequest['attributes']) => {
		try {
			await updateVoyagePayment(id, voyageId, attributes);
			refreshDetails();
			refresh?.();

			showSuccessNotification(
				'Payment saved',
			);
		} catch (e) {
			showErrorNotification('Could not edit payment', e as Error);
		}
	};

	const createPayment = async (values: {
		paymentDate: Moment;
		amount: number;
		parts: VoyagePaymentPartReturnType[];
		bankCharge?: number;
		exchangeRate?: number;
	}) => {
		const {
			paymentDate,
			amount: totalAmount,
			parts,
			bankCharge,
			exchangeRate: paymentExchangeRate,
		} = values;

		await createVoyagePayment(
			voyageId,
			paymentDate,
			totalAmount,
			parts,
			bankCharge ?? 0,
			isEua ? VoyagePaymentTypes.EUA : VoyagePaymentTypes.INVOICE,
			currency,
			paymentExchangeRate ?? 1,
		);
		refreshDetails();
		refresh?.();
	};

	const fixtureCurrency = useMemo(() => {
		if (isEua) {
			return Currencies.EUA;
		}

		return voyageDetails.bankAccount == null ?
			userInfo.baseCurrency :
			voyageDetails.bankAccount.currency;
	}, [isEua, userInfo.baseCurrency, voyageDetails.bankAccount]);

	const [currency, setCurrency] = useState<Values<typeof Currencies>>(fixtureCurrency);

	useEffect(() => {
		setCurrency(fixtureCurrency);
	}, [fixtureCurrency]);

	const columns = [
		{
			title: 'Date',
			dataIndex: 'paymentDate',
			sorter: standardSort('paymentDate'),
			defaultSortOrder: 'desc' as SortOrder,
			render: (date: Moment) => formatDate(date, DATE),
		},
		{
			align: 'right' as 'right',
			title: 'Amount',
			dataIndex: 'amount',
			render: (v: number, payment: PaymentsAndParts) => formatCurrency(v, payment.currency),
		},
		{
			title: '',
			align: 'right' as 'right',
			dataIndex: '',
			className: styles.editButton,
			render: (values: any, payment: PaymentsAndParts) => (
				<FormPopover<PaymentsAndParts>
					title="Edit payment"
					onSubmit={(v) => editPayment(
						payment.id,
						v,
					)}
					// @ts-ignore
					fields={newPaymentFields}
					initialValues={values}
					placement="bottom"
					onOpenChange={(visible) => {
						setFormAmount(visible ? values.amount : null);
						if (visible) {
							setCurrency(payment.currency);
						}
					}}
					popoverWidth={900}
					formProps={{
						layout: 'horizontal',
						labelAlign: 'left' as 'left',
						labelCol: { flex: '150px' },
						wrapperCol: { flex: 'auto' },
						colon: false,
					}}
					destroyTooltipOnHide
				>
					<Button
						className={styles.editButton}
						disabled={payment.externalId != null}
						disabledTooltip="This payment is synced with Xero and cannot be edited from ClearVoyage. Edit it from Xero instead."
						icon={(<EditOutlined />)}
						type="link"
					/>
				</FormPopover>
			),
		},
		{
			title: '',
			align: 'left' as 'left',
			dataIndex: '',
			render: (_: any, payment: PaymentsAndParts) => (
				<Button
					confirmTitle="Are you sure you want to delete this payment?"
					onClick={() => deletePayment(payment.id)}
					className={styles.deleteButton}
					icon={(<DeleteOutlined />)}
					disabledTooltip="This payment is synced with Xero and cannot be deleted from ClearVoyage. Delete it from Xero instead."
					disabled={payment.externalId != null}
					danger
					type="link"
				/>
			),
		},
	];

	const newPaymentFields = useMemo(() => (
		[
			{
				name: 'paymentDate',
				label: 'Payment date',
				type: 'date',
				required: true,
				inputProps: {},
			},
			{
				name: 'amount',
				label: 'Amount',
				type: 'currency',
				required: true,
				inputProps: {
					allowNegative: true,
					onChange: (v: number | null) => setFormAmount(v),
					currency,
				},
			},
			{
				name: 'currency',
				label: 'Currency',
				renderInput: () => (
					<Select
						value={currency}
						defaultValue={currency}
						options={uniqueCurrencies.map((c) => ({ label: c, value: c }))}
						onChange={(newValue) => setCurrency(newValue as Values<typeof Currencies>)}
					/>
				),
			},
			{
				name: 'exchangeRate',
				label: `Exchange Rate (${currency} to ${fixtureCurrency})`,
				type: 'custom',
				required: currency !== fixtureCurrency,
				hide: currency === fixtureCurrency,
				renderInput: () => (
					<PaymentExchangeRateInput
						currency={currency}
						fixtureCurrency={fixtureCurrency}
					/>
				),
			},
			{
				name: 'parts',
				label: 'Invoice(s)',
				type: 'custom',
				required: false,
				renderInput: () => (
					<PaymentInvoiceInput
						hireInvoices={hireInvoices}
						totalAmount={formAmount}
						currency={currency}
					/>
				),
				rules: [{
					validator: async (_rule: any, value: { hireInvoiceId: number; amount: number }[]) => validatePaymentInvoiceInput(
						formAmount,
						value,
						currency,
					),
				}],
			},
			...(isEua ? [] : [
				{
					type: 'currency' as 'currency',
					name: 'bankCharge',
					inputProps: {
						currency,
					},
					label: (
						<>
							Bank Charge
							<TooltipIcon>
								If a bank charge was subtracted from the payment, add it here.
								<br />
								Bank charges will be shown on the cumulative hire statement.
							</TooltipIcon>
						</>
					),
					required: false,
				},
			]),
		]
	), [currency, fixtureCurrency, formAmount, isEua, uniqueCurrencies, hireInvoices]);

	const onSync = async () => {
		try {
			await syncPayments(voyageId);
			refreshDetails();
		} catch (e) {
			showErrorNotification('Could not sync payments', e as Error);
		}
	};

	return (
		<Card
			slim
			title="Payments"
			className={styles.paginationPadding}
			extra={(
				<Space>
					{usesPaymentsSync && (fixtureType !== FixtureTypes.TC_IN || canSyncTcInPayments) && (
						<Button onClick={onSync} icon={(<SyncOutlined />)}>
							Sync payments
						</Button>
					)}
					<FormPopover<PaymentsAndParts>
						title="Add payment"
						onSubmit={createPayment}
						// @ts-ignore
						fields={newPaymentFields}
						placement="bottom"
						onOpenChange={(visible) => {
							setFormAmount(null);
							if (visible) {
								setCurrency(fixtureCurrency);
							}
						}}
						data-tour="createPaymentForm"
						popoverWidth={900}
						destroyTooltipOnHide
						formProps={{
							layout: 'horizontal',
							labelAlign: 'left',
							labelCol: { flex: '150px' },
							wrapperCol: { flex: 'auto' },
							colon: false,
						}}
					>
						<AddButton
							data-tour="createPaymentButton"
						>
							New Payment
						</AddButton>
					</FormPopover>
				</Space>
			)}
		>
			<Table<PaymentsAndParts>
				columns={columns}
				pagination={false}
				dataSource={payments}
				locale={{ emptyText: 'No payments registered' }}
				expandable={{
					expandedRowRender: (row) => {
						const partsTotal = calculateTotal(row.parts, (part) => part.amount);

						return (
							<div className={styles.expandedRow}>
								{row.bankCharge != null && row.bankCharge > 0 && (
									<>
										<strong>Bank charge:</strong>
										{' '}
										{formatCurrency(row.bankCharge, row.currency)}
										<hr />
									</>
								)}
								<Typography.Title level={4}>Payment was attributed to</Typography.Title>
								<div className={styles.partsWrapper}>
									{row.parts.map((part) => (
										<PaymentPartRow
											key={part.hireInvoiceNumber}
											identifier={part.hireInvoiceNumber}
											amount={part.amount}
											currency={row.currency}
										/>
									))}
									{row.amount > partsTotal && (
										<PaymentPartRow
											identifier="No invoice"
											amount={row.amount - partsTotal}
											currency={row.currency}
										/>
									)}
								</div>
							</div>
						);
					},
				}}
				rowKey="id"
				size="small"
			/>
		</Card>
	);
};

export default PaymentsTable;
